import React, { useEffect, useRef, useState } from 'react';
import Navigation from '@header/scripts/components/navigation/Navigation.jsx';
import HeaderCart from '@header/scripts/components/HeaderCart.jsx';
import WishlistNavigationHeader from '@wishlist/components/WishlistNavigationHeader.jsx';
import HeaderUser from '@header/scripts/components/HeaderUser.jsx';
import SearchField from '@header/scripts/components/search/SearchField.jsx';
import SearchFieldLegacy from '@header/scripts/components/search/SearchFieldLegacy.jsx';
import HeaderLogo from '@header/scripts/components/HeaderLogo.jsx';
import HeaderVoucher from '@header/scripts/components/HeaderVoucher.jsx';
import IntroPopover from '@header/scripts/components/IntroPopover.jsx';
import {safeStorageGet, safeStorageSet} from '@header/scripts/utils/storage';
import Expi, { ExperimentVariant } from '@core/scripts/modules/expi';
import {isSearchRedesignActive} from '@header/scripts/helper/search-redesign';
import {VoucherTrinityEvents} from '@voucher/scripts/utils/voucherTrinity.js';
import Trinity from '@core/scripts/components/trinity-adapter.js';

export default function Header({
    rootUrl,
    navigation,
    wishlist,
    cart,
    user,
    deviceType,
    notifications,
    currentPage,
    currentShopId,
    search,
}) {
    const [logoHidden, setLogoHidden] = useState(false);
    const [showVoucherBadge, setShowVoucherBadge] = useState(false);
    const [showVoucherIntro, setShowVoucherIntro] = useState(false);
    const [activePopover, setActivePopover] = useState(null);

    const headerRef = useRef();
    const voucherButtonRef = useRef(null);

    const VOUCHER_BADGE_SEEN_STORAGE_KEY = 'voucherBadgeSeen';

    const isVoucherServiceEnabled = Expi.getVariant('EXPI-186') === ExperimentVariant.VARIANT_B;
    // PDS-400: the redesigned onsite search is on for de_at via Unleash, and in de_de for EXPI-214 variant b.
    const isSearchRedesignEnabled = isSearchRedesignActive();

    useEffect(() => {
        const hasSeenVoucherIntro = safeStorageGet(window?.localStorage, VOUCHER_BADGE_SEEN_STORAGE_KEY) === 'true';
        const shouldShowVoucherIntro = isVoucherServiceEnabled && !hasSeenVoucherIntro;
        setShowVoucherBadge(shouldShowVoucherIntro);
        setShowVoucherIntro(shouldShowVoucherIntro);

        if (shouldShowVoucherIntro) {
            setActivePopover('voucher');
        } else if (notifications?.cartItemsPresentInNewSessionCount > 0) {
            setActivePopover('cart');
        } else if (notifications?.wishlistItemsPresentInNewSessionCount > 0) {
            setActivePopover('wishlist');
        }
    }, []);

    const markVoucherIntroSeen = () => {
        if (!isVoucherServiceEnabled) {
            return;
        }
        if (showVoucherBadge) {
            setShowVoucherBadge(false);
        }
        if (showVoucherIntro) {
            setShowVoucherIntro(false);
        }
        if (activePopover === 'voucher') {
            setActivePopover(null);
        }
        safeStorageSet(window?.localStorage, VOUCHER_BADGE_SEEN_STORAGE_KEY, 'true');
    };

    const hideLogo = () => {
        setLogoHidden(true);
    };
    const showLogo = () => {
        setLogoHidden(false);
    };


    const openVoucherModal = () => {
        markVoucherIntroSeen();
        VoucherTrinityEvents.sendWalletIconClick();
        window.dispatchEvent(new CustomEvent('voucher-modal:open'));
    };

    const trinityHeaderHintClose = (icon) => {
        Trinity.sendTrinityEvent({
            schema: {version: 1, name: "CLICK_HEADER_HINT_CLOSE"},
            attributes: {
                clickName: "closeHint",
                icon: icon
            }
        });
        setActivePopover(null);
    };

    const trinityHeaderHintOpen = (icon) => {
        Trinity.sendTrinityEvent({
            schema: {version: 2, name: "VIEW_HEADER_HINT"},
            attributes: {
                viewName: "headerHint",
                hintType: "savedStyles",
                icon: icon
            }
        })
    }

    return (
        <div className="header__inner-wrapper" ref={headerRef}>
            <div className="header__left">
                {!logoHidden && <HeaderLogo rootUrl={rootUrl} />}

                <Navigation
                    navigation={navigation}
                    currentPage={currentPage}
                    currentShopId={currentShopId}
                    headerRef={headerRef}
                />
            </div>
            <div className="header__right">
                {isSearchRedesignEnabled ? (
                    <SearchField
                        searchUrl={search.url}
                        noResultsFound={search.noResultsFound}
                        hideLogo={hideLogo}
                        showLogo={showLogo}
                    />
                ) : (
                    <SearchFieldLegacy
                        searchUrl={search.url}
                        noResultsFound={search.noResultsFound}
                        hideLogo={hideLogo}
                        showLogo={showLogo}
                    />
                )}

                <HeaderUser user={user} />
                {isVoucherServiceEnabled && (
                    <div className="header__voucher-wrapper">
                        <HeaderVoucher
                            ref={voucherButtonRef}
                            onClick={openVoucherModal}
                            ariaControls="voucher-dialog"
                            showBadge={showVoucherBadge}
                        />
                        {activePopover === "voucher" && <IntroPopover
                            popoverSeen={() => trinityHeaderHintOpen("wallet")}
                            isOpen={true}
                            onClose={markVoucherIntroSeen}
                            ariaLabel="Gutschein-Einführung"
                        >
                            <div className="intro-popover__title" onClick={openVoucherModal}>Deine neue
                                Gutscheinübersicht ist da!
                            </div>
                            <div onClick={openVoucherModal}>Ab sofort findest Du <span
                                className="intro-popover__text-link">hier</span> all Deine Rabatte.
                            </div>
                        </IntroPopover>}
                    </div>
                )}
                <div className="relative flex items-center">
                <WishlistNavigationHeader
                    wishlistUrl={wishlist.url}
                    wishlistCount={wishlist.count}
                    deviceType={deviceType}
                    texts={wishlist.texts}
                    enabled={wishlist.enabled}
                />
                    {activePopover === 'wishlist' && <IntroPopover
                        popoverSeen={() => trinityHeaderHintOpen('wishlist')}
                        isOpen={true}
                        popoverRight={"-1.8rem"}
                        onClose={() => trinityHeaderHintClose('wishlist')}
                        ariaLabel="Hinweis zur Wunschliste"
                    >
                        <div className="intro-popover__title">Du hast Artikel auf Deiner Wunschliste!</div>
                        <div>Schau sie Dir jetzt an.</div>
                    </IntroPopover>}
                </div>
                <div className="relative flex items-center">
                <HeaderCart url={cart.url} title={cart.title} deviceType={deviceType} />
                {activePopover === "cart" && <IntroPopover
                    popoverSeen={() => trinityHeaderHintOpen('cart')}
                    isOpen={true}
                    popoverRight={"0.2rem"}
                    onClose={() => trinityHeaderHintClose('cart')}
                    ariaLabel="Hinweis zum Warenkorb"
                >
                    <div  className="intro-popover__title">Du hast noch Artikel im Warenkorb!</div>
                    <div>Schließe Deinen Einkauf jetzt ab.</div>
                </IntroPopover>}
                </div>
            </div>
        </div>
    );
}
